import { Button as ChakraButton, Spinner } from '@chakra-ui/react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
  [key: string]: unknown;
}

const variantMap: Record<ButtonVariant, Record<string, string>> = {
  primary: {
    bg: 'interactive.default',
    color: 'text.onBrand',
    borderColor: 'interactive.default',
    _hover: 'interactive.hover',
  },
  secondary: {
    bg: 'surface.primary',
    color: 'text.secondary',
    borderColor: 'border.primary',
    _hover: 'bg.active',
  },
  ghost: {
    bg: 'transparent',
    color: 'text.secondary',
    borderColor: 'transparent',
    _hover: 'bg.active',
  },
  danger: {
    bg: 'status.error.bg',
    color: 'status.error.text',
    borderColor: 'status.error.border',
    _hover: 'status.error.bg',
  },
};

export function Button({
  variant = 'primary',
  loading = false,
  icon,
  fullWidth = false,
  children,
  disabled,
  style,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const v = variantMap[variant];

  return (
    <ChakraButton
      {...props}
      disabled={isDisabled}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="2"
      px="4"
      py="3"
      borderRadius="md"
      fontWeight="500"
      fontSize="14px"
      lineHeight="20px"
      border="1px solid"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      minH="40px"
      width={fullWidth ? 'full' : undefined}
      opacity={isDisabled ? 0.5 : 1}
      bg={v.bg}
      color={v.color}
      borderColor={v.borderColor}
      transition="background-color 150ms ease, color 150ms ease, border-color 150ms ease, opacity 150ms ease"
      _hover={isDisabled ? {} : { bg: v._hover }}
      style={style}
    >
      {loading ? (
        <Spinner size="xs" borderWidth="2px" />
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </ChakraButton>
  );
}
